import { Alert, AlertActionCloseButton, Spinner } from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'

import { RequireFeatureFlag } from '@app/components/feature_flags/require-feature-flag'
import { ModifyInteractionModal } from '@app/components/program/modify_interaction_modal'
import { SelectConfigurationDefinitionModal } from '@app/components/program/select-configuration-modal'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import { useProgram } from '@app/domain/program/program.hooks'
import type {
  IProgramId,
  IProgramInteraction,
  IProgramInteractionId,
  ProgramInteractionTypeId,
} from '@app/domain/program/program.interface'
import * as ProgramScreenState from '@app/state/program_screen/state'

import { AdHocInteractionSection } from './ad-hoc-interaction-section'
import { FollowUpInteractionSection } from './follow-up-interaction-section'
import { InitialInteraction } from './initial-interaction'
import { ManageReportTemplatesModal } from './manage-report-templates-modal'
import { RemoteInteractionSection } from './remote-interaction-section'

interface Props {
  programId?: IProgramId
}

export const ProgramInteractions = ({ programId }: Props) => {
  const [modalTitle, setModalTitle] = useState<string>(undefined)
  const [configurationModalTitle, setConfigurationModalTitle] = useState<
    string
  >(undefined)

  const dispatch = useDispatch()

  const {
    program,
    errorMessage,
    clearError,
    addReportTemplate,
    deleteReportTemplate,
    addProgramInteraction,
    removeProgramInteraction,
    updateProgramInteraction,
    updateProgramInteractionProperties,
    addProgramInteractionWithDefinitionId,
    updateProgramInteractionWithDefinitionId,
  } = useProgram(programId)

  const [currentInteraction, setCurrentInteraction] = useState<
    IProgramInteraction | undefined
  >(undefined)

  const [currentInteractionType, setCurrentInteractionType] = useState<
    ProgramInteractionTypeId | undefined
  >(undefined)

  const [
    isManageReportTemplateModalOpen,
    setIsManageReportTemplateModalOpen,
  ] = useState(false)

  const [isEditInteractionModalOpen, setIsEditInteractionModalOpen] = useState<
    boolean
  >(false)

  const [
    isSelectConfigurationforInteractionModalOpen,
    setIsSelectConfigurationforInteractionModalOpen,
  ] = useState(false)

  const onAddOrUpdateInteraction = useCallback(
    (
      selectedInstruments: string[],
      interactionType: ProgramInteractionTypeId,
      interactionId?: string,
    ) => {
      if (interactionId) {
        updateProgramInteraction(interactionId, selectedInstruments)
      } else {
        addProgramInteraction(interactionType, selectedInstruments)
      }
      setIsEditInteractionModalOpen(false)
    },
    [addProgramInteraction, updateProgramInteraction],
  )

  const handleAddOrUpdateDefinitionConfigurationForInteraction = useCallback(
    (definitionId: string) => {
      if (currentInteraction?.id) {
        updateProgramInteractionWithDefinitionId(
          currentInteraction.id,
          definitionId,
        )
      } else {
        addProgramInteractionWithDefinitionId(
          currentInteractionType,
          definitionId,
        )
      }

      setIsSelectConfigurationforInteractionModalOpen(false)
    },
    [
      currentInteraction?.id,
      currentInteractionType,
      addProgramInteractionWithDefinitionId,
      updateProgramInteractionWithDefinitionId,
    ],
  )

  const handleEditInteractionProperties = useCallback(
    (
      programInteractionId: string,
      label?: string,
      daysAfterInitialInteraction?: number,
    ) => {
      updateProgramInteractionProperties(
        programInteractionId,
        label,
        daysAfterInitialInteraction,
      )
    },
    [updateProgramInteractionProperties],
  )

  const handleDeleteInteraction = useCallback(
    (programInteractionId: IProgramInteractionId) => {
      removeProgramInteraction(programInteractionId)
    },
    [removeProgramInteraction],
  )

  const handleOpenInteractionModal = useCallback(
    (
      modalTitle: string,
      programInteractionTypeId: ProgramInteractionTypeId,
      programInteraction: IProgramInteraction,
    ) => {
      setModalTitle(modalTitle)
      setIsEditInteractionModalOpen(true)
      setCurrentInteraction(programInteraction)
      setCurrentInteractionType(programInteractionTypeId)
    },
    [],
  )

  const handleCloseInteractionModal = useCallback(() => {
    setIsEditInteractionModalOpen(false)
  }, [])

  const handleOpenManageReportTemplateModal = useCallback(
    (programInteraction: IProgramInteraction) => {
      setCurrentInteraction(programInteraction)
      setIsManageReportTemplateModalOpen(true)
    },
    [],
  )

  const handleOpenSelectConfigurationModal = useCallback(
    (
      modalTitle: string,
      programInteractionTypeId: ProgramInteractionTypeId,
      programInteraction: IProgramInteraction,
    ) => {
      setConfigurationModalTitle(modalTitle)
      setIsSelectConfigurationforInteractionModalOpen(true)
      setCurrentInteraction(programInteraction)
      setCurrentInteractionType(programInteractionTypeId)
    },
    [],
  )

  const handleCloseManageReportTemplateModal = useCallback(() => {
    setIsManageReportTemplateModalOpen(false)
  }, [])

  const handleCloseSelectConfigurationModal = useCallback(() => {
    setIsSelectConfigurationforInteractionModalOpen(false)
  }, [])

  const handleAddReportTemplate = useCallback(
    async (
      programInteractionId: string,
      templateId: string,
      label?: string,
    ) => {
      addReportTemplate(programInteractionId, templateId, label)
    },
    [addReportTemplate],
  )

  const handleDeleteReportTemplate = useCallback(
    async (programInteractionId: string, templateId: string) => {
      deleteReportTemplate(programInteractionId, templateId)
    },
    [deleteReportTemplate],
  )

  const handleUpdateExpiration = useCallback(
    (newExpiration: string) => {
      if (program) {
        dispatch(
          ProgramScreenState.actions.updateProgram({
            ...program,
            remoteInteractionExpirationPeriod: newExpiration,
          }),
        )
      }
    },
    [dispatch, program],
  )
  if (!program) {
    return <Spinner size="sm" />
  }

  return (
    <>
      <ManageReportTemplatesModal
        isModalOpen={isManageReportTemplateModalOpen}
        programInteraction={currentInteraction}
        addReportTemplate={handleAddReportTemplate}
        onClose={handleCloseManageReportTemplateModal}
      />
      <ModifyInteractionModal
        isModalOpen={isEditInteractionModalOpen}
        addOrUpdateInteraction={onAddOrUpdateInteraction}
        onCancel={handleCloseInteractionModal}
        interaction={currentInteraction}
        interactionType={currentInteractionType}
        title={modalTitle}
      />
      <SelectConfigurationDefinitionModal
        isModalOpen={isSelectConfigurationforInteractionModalOpen}
        selectedDefinitionId={currentInteraction?.visitDefinitionId}
        onSave={handleAddOrUpdateDefinitionConfigurationForInteraction}
        onCancel={handleCloseSelectConfigurationModal}
        title={configurationModalTitle}
      />
      {errorMessage && (
        <Alert
          variant="danger"
          title={errorMessage}
          aria-live="polite"
          isInline
          actionClose={<AlertActionCloseButton onClose={clearError} />}
        />
      )}
      <RequireFeatureFlag
        key="initial_followup_interaction"
        feature={FeatureFlagId.InitialAndFollowUpInteraction}
      >
        <InitialInteraction
          onEditInteraction={handleOpenInteractionModal}
          program={program}
        />
        <FollowUpInteractionSection
          onDeleteInteraction={handleDeleteInteraction}
          onEditInteraction={handleOpenInteractionModal}
          onEditInteractionProperties={handleEditInteractionProperties}
          program={program}
        />
      </RequireFeatureFlag>
      <RequireFeatureFlag
        key="adhoc_interaction"
        feature={FeatureFlagId.AdHocInteraction}
      >
        <AdHocInteractionSection
          onDeleteInteraction={handleDeleteInteraction}
          onEditInteraction={handleOpenInteractionModal}
          onEditInteractionLabel={handleEditInteractionProperties}
          program={program}
        />
      </RequireFeatureFlag>
      <RequireFeatureFlag
        key="remote_instrument"
        feature={FeatureFlagId.RemoteInteraction}
      >
        <RemoteInteractionSection
          onDeleteInteraction={handleDeleteInteraction}
          onDeleteReportTemplate={handleDeleteReportTemplate}
          onEditInteraction={handleOpenSelectConfigurationModal}
          onEditInteractionProperties={handleEditInteractionProperties}
          program={program}
          onEditInteractionReportTemplates={handleOpenManageReportTemplateModal}
          onEditExpirationPeriod={handleUpdateExpiration}
        />
      </RequireFeatureFlag>
    </>
  )
}
